import { keyframes, styled } from '@mui/material';
import { Link } from 'react-router';

import { I18nResources, useI18n } from '@app-velocity/i18n';

import { BackgroundBlur } from './BackgroundBlur';
import { Badge } from './Badge';
import { Button } from '../Button';
import { Typography } from '../Typography';

export type SignUpNoticeProps = {
  title: I18nResources;
  text: I18nResources;
  action: I18nResources;
  badge: string;
};

const fadeIn = keyframes`
  0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
`;

const NoticeContainer = styled('div')({
  position: 'absolute',
  bottom: 0,
  left: 0,
  width: '100%',
  zIndex: 1,
});

const NoticeContent = styled('div')(({ theme }) => ({
  textAlign: 'center',
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexDirection: 'column',
  gap: theme.spacing(4),
  padding: theme.spacing(6),
  zIndex: 10,
  animation: `${fadeIn} 0.5s cubic-bezier(0.5, 0, 0.5, 1)`,
  background:
    'linear-gradient(0deg, var(--background-background-secondary-color) 70%,rgba(241, 243, 246, 0) 100%);',
}));

const StyledDescription = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexDirection: 'column',
  textAlign: 'center',
  gap: theme.spacing(1),
}));

const StyledTitle = styled(Typography)({
  fontSize: 14,
  fontWeight: 600,
  color: 'var(--text-text-primary-color)',
});

const StyledText = styled(Typography)({
  fontSize: 13,
  fontWeight: 300,
  color: 'var(--text-text-tertiary-color)',
});

export const SignUpNotice = ({
  title,
  text,
  badge,
  action,
}: SignUpNoticeProps) => {
  const { t } = useI18n();

  return (
    <NoticeContainer>
      <NoticeContent>
        <Badge text={badge} />
        <StyledDescription>
          <StyledTitle component="h2">{t(title)}</StyledTitle>
          <StyledText component="p">{t(text)}</StyledText>
        </StyledDescription>
        <Button
          variant="cta"
          component={Link}
          data-ph-capture-attribute-id="sign_up_after_guest_action"
          to="/registration"
          size="large"
          fullWidth
        >
          {t(action)}
        </Button>
      </NoticeContent>
      <BackgroundBlur />
    </NoticeContainer>
  );
};
